import type { ComponentType } from 'react';
import { createBrowserRouter } from 'react-router';
import { RootErrorPage } from '@/pages/RootErrorPage';
import { AppLayout } from './layouts/AppLayout';
import { MarketingLayout } from './layouts/MarketingLayout';
import { RequireSession } from './RequireSession';
import { RootProviders } from './RootProviders';

// Pages use named exports, so `lazy` maps them to `Component` explicitly.
const page = (load: () => Promise<Record<string, unknown>>, name: string) => async () => ({
  Component: (await load())[name] as ComponentType,
});

export const routes = [
  {
    element: <RootProviders />,
    errorElement: <RootErrorPage />,
    // Shown while the first lazy route loads (no spinner flash; same canvas as the app).
    hydrateFallbackElement: <div className="min-h-dvh bg-canvas" aria-busy="true" />,
    children: [
      {
        element: <MarketingLayout />,
        children: [
          { path: '/', lazy: page(() => import('@/pages/LandingPage'), 'LandingPage') },
          { path: '/privacy', lazy: page(() => import('@/pages/LegalPage'), 'PrivacyPage') },
          { path: '/terms', lazy: page(() => import('@/pages/LegalPage'), 'TermsPage') },
        ],
      },
      {
        element: <AppLayout />,
        children: [
          // Guests allowed (isGuest challenges only).
          {
            path: '/learn/:track/:chapter/:challenge',
            lazy: page(() => import('@/pages/ChallengePage'), 'ChallengePage'),
          },
          {
            element: <RequireSession />,
            children: [
              { path: '/learn', lazy: page(() => import('@/pages/DashboardPage'), 'DashboardPage') },
              { path: '/practice', lazy: page(() => import('@/pages/PracticePage'), 'PracticePage') },
              { path: '/profile', lazy: page(() => import('@/pages/ProfilePage'), 'ProfilePage') },
              { path: '/recap/:reviewId', lazy: page(() => import('@/pages/RecapPage'), 'RecapPage') },
            ],
          },
        ],
      },
      { path: '/auth/callback', lazy: page(() => import('@/pages/AuthCallbackPage'), 'AuthCallbackPage') },
      ...(import.meta.env.DEV
        ? [{ path: '/__ui', lazy: page(() => import('@/pages/UiPage'), 'UiPage') }]
        : []),
      { path: '*', lazy: page(() => import('@/pages/NotFoundPage'), 'NotFoundPage') },
    ],
  },
];

export const router = createBrowserRouter(routes);
